import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Sparkles, TrendingUp, Smartphone } from 'lucide-react';

import featureExpenses from '@/assets/feature-expenses.png';
import featureReports from '@/assets/feature-reports.png';
import featureAiStrategy from '@/assets/feature-ai-strategy.png';
import featureProjections from '@/assets/feature-projections.png';
import featureMobile from '@/assets/feature-mobile.png';

const features = [
  {
    title: "Never Miss an Expense",
    subtitle: "Complete expense tracking",
    description: "Our calculator includes every expense category that matters—property taxes, insurance, vacancy, repairs, property management, and detailed utility breakdowns. No more surprise costs eating into your profits.",
    bullets: [
      "All major expense categories pre-built",
      "Utility breakdown (water, sewer, electric, gas)",
      "Monthly or annual input modes"
    ],
    image: featureExpenses,
    icon: CheckCircle2
  },
  {
    title: "AI-Powered Investment Strategies",
    subtitle: "Let AI write your pitch",
    description: "Generate professional investment strategy narratives with one click. Our AI analyzes your deal metrics and creates compelling descriptions perfect for partners and lenders.",
    bullets: [
      "Trained on proven investor strategies",
      "Incorporates your actual deal numbers",
      "Perfect for pitch decks and presentations"
    ],
    image: featureAiStrategy,
    icon: Sparkles
  },
  {
    title: "Create Clean & Compelling Reports",
    subtitle: "Professional-grade presentations",
    description: "Transform your analysis into stunning, investor-ready reports that command attention. Whether you're presenting to partners, pitching to lenders, or building your own portfolio records—your deals deserve to look as good as they are.",
    bullets: [
      "One-click PDF export with your branding",
      "AI-generated investment strategy narratives",
      "Shareable reports that impress partners and lenders"
    ],
    image: featureReports,
    icon: FileText
  },
  {
    title: "See Your Future Wealth",
    subtitle: "30-year projections",
    description: "Watch your investment grow over time with detailed year-by-year projections. See equity buildup, cash flow accumulation, and total returns—so you know exactly what you're building.",
    bullets: [
      "Year-by-year equity growth",
      "Cumulative cash flow tracking",
      "Total ROI and wealth projection"
    ],
    image: featureProjections,
    icon: TrendingUp
  },
  {
    title: "Analyze Deals on the Go",
    subtitle: "Mobile-friendly design",
    description: "Spot a deal while driving for dollars? Run the numbers right from your phone. Our fully responsive interface works beautifully on any device, so you never miss an opportunity—even when you're away from your desk.",
    bullets: [
      "Fully responsive on phones and tablets",
      "Run complete analyses from anywhere",
      "Same powerful features, pocket-sized"
    ],
    image: featureMobile,
    icon: Smartphone
  }
];

export function FeatureShowcase() {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
            Everything you need to <span className="text-primary">analyze like a pro</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Built from real investor workflows, every feature is designed to help you make confident decisions faster.
          </p>
        </motion.div>

        <div className="space-y-20 md:space-y-32">
          {features.map((feature, index) => {
            const isReversed = index % 2 === 1;
            const Icon = feature.icon;

            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* Image */}
                <div className={`relative ${isReversed ? 'lg:order-2' : ''}`}>
                  <div className={`relative rounded-2xl overflow-hidden shadow-xl border-2 border-border/50 bg-card ring-1 ring-border/20 ${feature.cropImage ? 'max-h-[400px]' : ''}`}>
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={`w-full ${feature.cropImage ? 'h-[400px] object-cover object-top' : 'h-auto'}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/5 via-transparent to-transparent" />
                  </div>
                  <div className={`absolute -z-10 w-72 h-72 rounded-full bg-primary/5 blur-3xl ${isReversed ? '-right-20 -bottom-20' : '-left-20 -bottom-20'}`} />
                </div>

                {/* Content */}
                <div className={isReversed ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-primary">{feature.subtitle}</span>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
                    {feature.title}
                  </h3>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {feature.description}
                  </p>

                  <ul className="space-y-3">
                    {feature.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
